/**
 * @file useOnClickOrScrollOutside.ts
 * @description A versatile hook that detects clicks and (optionally) scrolls outside a specified element
 * (and optionally a portal). Defaults to clicks only, ensuring old usage still works.
 *
 * Old usage (scroll ignored by default):
 *    useOnClickOrScrollOutside(ref, handler)
 *    useOnClickOrScrollOutside(ref, handler, 'mouseup')
 *    useOnClickOrScrollOutside(ref, handler, portalRef)
 *    useOnClickOrScrollOutside(ref, handler, portalRef, 'mouseup')
 *
 * New usage enabling scroll detection:
 *    useOnClickOrScrollOutside(ref, handler, true)  // detect scroll, 'mousedown' event
 *    useOnClickOrScrollOutside(ref, handler, 'mouseup', true)  // detect scroll, 'mouseup' event
 *    useOnClickOrScrollOutside(ref, handler, portalRef, true)
 *    useOnClickOrScrollOutside(ref, handler, portalRef, 'mouseup', true)
 */

import { RefObject, useEffect } from 'react';
import useEventListener from './useEventListener';

type Handler = (event: Event) => void;

function useOnClickOrScrollOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  handler: Handler,
  /**
   * The third parameter can be:
   *  - a string 'mousedown'/'mouseup' for old usage,
   *  - a portal RefObject<HTMLElement> for new usage,
   *  - a boolean (detectScroll) to enable/disable scroll detection.
   */
  portalOrMouseEventOrScroll?:
    | RefObject<HTMLElement>
    | 'mousedown'
    | 'mouseup'
    | boolean,
  /**
   * The fourth parameter can be:
   *  - a string 'mousedown'/'mouseup' if the third was a portal,
   *  - a boolean detectScroll if the third was a portal or mouseEvent,
   *  - or undefined (no usage).
   */
  maybeMouseEventOrScroll?: 'mousedown' | 'mouseup' | boolean,
): void {
  let portalRef: RefObject<HTMLElement> | null = null;
  let mouseEvent: 'mousedown' | 'mouseup' = 'mousedown';
  let detectScroll = false;

  // Parse the third argument.
  if (portalOrMouseEventOrScroll !== undefined) {
    if (typeof portalOrMouseEventOrScroll === 'string') {
      // e.g. useOnClickOrScrollOutside(ref, handler, 'mouseup')
      mouseEvent = portalOrMouseEventOrScroll;
    } else if (typeof portalOrMouseEventOrScroll === 'boolean') {
      // e.g. useOnClickOrScrollOutside(ref, handler, true)
      detectScroll = portalOrMouseEventOrScroll;
    } else {
      // e.g. useOnClickOrScrollOutside(ref, handler, portalRef)
      portalRef = portalOrMouseEventOrScroll;
    }
  }

  // Parse the fourth argument (if any).
  if (maybeMouseEventOrScroll !== undefined) {
    if (typeof maybeMouseEventOrScroll === 'string') {
      // e.g. useOnClickOrScrollOutside(ref, handler, portalRef, 'mouseup')
      mouseEvent = maybeMouseEventOrScroll;
    } else if (typeof maybeMouseEventOrScroll === 'boolean') {
      // e.g. useOnClickOrScrollOutside(ref, handler, portalRef, true)
      detectScroll = maybeMouseEventOrScroll;
    }
  }

  // 1) Click outside detection
  useEventListener(mouseEvent, (event) => {
    const el = ref?.current;
    const portalEl = portalRef?.current;

    // Do nothing if clicking inside the main ref or the optional portal
    if (
      (el && el.contains(event.target as Node)) ||
      (portalEl && portalEl.contains(event.target as Node))
    ) {
      return;
    }
    handler(event);
  });

  // 2) (Optional) Scroll outside detection
  useEffect(() => {
    if (!detectScroll) return; // by default, do NOT detect scrolls

    const el = ref?.current;
    if (!el) return;

    const portalEl = portalRef?.current;

    const getScrollParents = (node: HTMLElement): (Element | Window)[] => {
      const scrollParents: (Element | Window)[] = [];
      let parent: HTMLElement | null = node.parentElement;
      while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          scrollParents.push(parent);
        }
        parent = parent.parentElement;
      }
      scrollParents.push(window);
      return scrollParents;
    };

    const scrollParents = getScrollParents(el);

    const handleScroll = (event: Event) => {
      if (
        el.contains(event.target as Node) ||
        (portalEl && portalEl.contains(event.target as Node))
      ) {
        return;
      }
      handler(event);
    };

    scrollParents.forEach((scrollParent) => {
      (scrollParent as Element | Window).addEventListener(
        'scroll',
        handleScroll,
      );
    });

    return () => {
      scrollParents.forEach((scrollParent) => {
        (scrollParent as Element | Window).removeEventListener(
          'scroll',
          handleScroll,
        );
      });
    };
  }, [ref, portalRef, handler, detectScroll]);
}

export default useOnClickOrScrollOutside;
